import React, { useState, useEffect } from 'react'
import { Tooltip, OverlayTrigger, Col, Form, Row } from "react-bootstrap"
import { Formik, useFormikContext } from "formik"
import InputMask from "react-input-mask"
import Select from "react-select"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScroll, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { schemaCreator, isFieldVisible, normalizeRfidTag } from "../assets/functions"

const normalizeVacancyValue = (value) => String(value ?? '').trim();
const isFieldRequired = (field) => field?.required === true || field?.primary === true;

const FieldLabel = ({ field }) => (
    <>
        {field?.label}
        {normalizeVacancyValue(field?.label) !== '' && isFieldRequired(field) && (
            <span className="text-danger ms-1" aria-hidden="true">*</span>
        )}
    </>
);

const getVacancyPayload = (dataApplication) => {
    const rawRows = dataApplication?.form_field_vacancies;
    if (Array.isArray(rawRows)) return rawRows;
    if (typeof rawRows !== 'string') return null;

    try {
        const parsedRows = JSON.parse(rawRows);
        return Array.isArray(parsedRows) ? parsedRows : null;
    } catch (_) {
        return null;
    }
};

const getVacancyRows = (dataApplication, field) => {
    const fieldID = normalizeVacancyValue(field?.inputID);
    const vacancyRows = getVacancyPayload(dataApplication);
    return vacancyRows
        ? vacancyRows.filter(
            (row) => normalizeVacancyValue(row?.field_id ?? row?.fieldId) === fieldID,
        )
        : [];
};

const getAvailabilityState = (row) => {
    if (row?.remaining !== undefined && row?.remaining !== null && row.remaining !== '') {
        const remaining = Number(row.remaining);
        return Number.isFinite(remaining) ? remaining > 0 : null;
    }

    const availabilityValue = row?.available ?? row?.is_available ?? row?.isAvailable;
    if (availabilityValue === undefined || availabilityValue === null || availabilityValue === '') {
        return null;
    }

    if (typeof availabilityValue === 'string') {
        return ['1', 'true', 'yes', 'sim'].includes(availabilityValue.toLowerCase());
    }

    return Boolean(availabilityValue);
};

const getVacancyAvailability = (dataApplication, field, option, currentValue, allowCurrentSelection = false) => {
    if (field?.vacancyEnabled !== true) return null;

    const normalizedOption = normalizeVacancyValue(option);
    const vacancyRows = getVacancyRows(dataApplication, field);
    const row = vacancyRows.find(
        (candidate) => normalizeVacancyValue(candidate?.option_value ?? candidate?.option) === normalizedOption,
    );
    const currentValues = Array.isArray(currentValue) ? currentValue : [currentValue];
    const isCurrentSelection = currentValues.some(
        (value) => normalizeVacancyValue(value) === normalizedOption,
    );

    if (!row) {
        return getVacancyPayload(dataApplication) !== null
            ? { isAvailable: false }
            : null;
    }

    const availabilityState = getAvailabilityState(row);
    if (availabilityState === null) return null;

    return {
        isAvailable: availabilityState || (allowCurrentSelection && isCurrentSelection),
    };
};

const getFirstAvailableVacancyOption = (dataApplication, field) => (
    Array.isArray(field?.options)
        ? field.options.find((option) => {
            const availability = getVacancyAvailability(dataApplication, field, option, '', false);
            return !availability || availability.isAvailable;
        }) || ''
        : ''
);

export const FormObserver = ({ fields, dataApplication }) => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
        fields.forEach(field => {
            if (!isFieldVisible(field, fields, values, new Set(), true)) {
                // Determine the key for the field
                const id = field.inputID.toString()

                // Prevent infinite loop by checking if the value already assumes an empty state
                const currentValue = values[id];

                if (Array.isArray(currentValue) && currentValue.length > 0) {
                    setFieldValue(id, []);
                } else if (currentValue !== "" && currentValue !== undefined && currentValue !== null && currentValue !== false) {
                    if (field.type === 'checkbox' && (!field.options || field.options.length === 0)) {
                        if (currentValue !== false) {
                            setFieldValue(id, false);
                        }
                    } else if (field.type === 'select' && field.options && field.options.length > 0) {
                        const defaultSelectValue = getFirstAvailableVacancyOption(dataApplication, field);
                        if (currentValue !== defaultSelectValue) {
                            setFieldValue(id, defaultSelectValue);
                        }
                    } else {
                        setFieldValue(id, "");
                    }
                }
            }
        });
    }, [values, fields, dataApplication, setFieldValue]);

    return null;
};

const normalizeFormRfidValues = (rawValues, formFields) => {
    if (!rawValues || !Array.isArray(formFields)) return rawValues;
    const updated = { ...rawValues };
    formFields.forEach((field) => {
        const key = field.inputID?.toString();
        if ((field.rfigtag === true || field.validation === 'rfid') && typeof updated[key] === 'string' && updated[key].trim() !== '') {
            updated[key] = normalizeRfidTag(updated[key]);
        }
    });
    return updated;
};

export const FormComponent = ({ dataApplication, formikRef, registerUser, editValues = false, type, showFullFormStatus, availableExhibitors = [], loadingAvailableExhibitors = false }) => {
    const [showFullForm, setShowFullForm] = useState(showFullFormStatus)

    useEffect(() => {
        setShowFullForm(showFullFormStatus)
    }, [showFullFormStatus, editValues?.user_id, editValues?.visitante_id])

    const fields = dataApplication.platform === 2 && type > 0
        ? dataApplication.json.form.fieldsUsers
        : dataApplication.json.form.fields

    const getInitialValues = () => {
        let values = {};

        fields.forEach((field) => {
            const inputID = field.inputID;
            const editValue = editValues ? editValues[inputID] : undefined;
            const isEditMode = Boolean(editValues);
            const hasEditValue = editValues
                && Object.prototype.hasOwnProperty.call(editValues, inputID)
                && editValue !== undefined
                && editValue !== null
                && (field.type !== 'select'
                    || typeof editValue !== 'string'
                    || editValue.trim() !== '');

            if (hasEditValue) {
                // Ensure array for multiple choice checkboxes
                if (field.type === 'checkbox' && field.allowMultipleChoice) {
                    if (Array.isArray(editValue)) {
                        values[inputID] = editValue;
                    } else if (typeof editValue === 'string' && editValue.length > 0) {
                        try {
                            const parsed = JSON.parse(editValue);
                            values[inputID] = Array.isArray(parsed) ? parsed : [editValue];
                        } catch (e) {
                            values[inputID] = [editValue];
                        }
                    } else {
                        values[inputID] = [];
                    }
                } else if (field.type === 'checkbox' && !field.allowMultipleChoice && field.options && field.options.length > 0) {
                    // For single choice multi-option checkbox, ensure it's not an array
                    if (Array.isArray(editValue)) {
                        values[inputID] = editValue[0] || "";
                    } else {
                        values[inputID] = editValue;
                    }
                } else {
                    values[inputID] = editValue;
                }
            } else if (field.type === 'select') {
                const hasConfiguredDefault = field.defaultValue !== undefined
                    && field.defaultValue !== null
                    && String(field.defaultValue).trim() !== '';
                const configuredDefaultOption = hasConfiguredDefault && Array.isArray(field.options)
                    ? field.options.find(
                        (option) => normalizeVacancyValue(option) === normalizeVacancyValue(field.defaultValue),
                    )
                    : undefined;
                const defaultAvailability = hasConfiguredDefault
                    && configuredDefaultOption !== undefined
                    ? getVacancyAvailability(dataApplication, field, configuredDefaultOption, configuredDefaultOption)
                    : null;

                values[inputID] = !isEditMode
                    && hasConfiguredDefault
                    && configuredDefaultOption !== undefined
                    && (!defaultAvailability || defaultAvailability.isAvailable)
                    ? configuredDefaultOption
                    : "";
            } else if (field.defaultValue) {
                // Handle default values for arrays
                if (field.type === 'checkbox' && field.allowMultipleChoice) {
                    values[inputID] = Array.isArray(field.defaultValue) ? field.defaultValue : [field.defaultValue];
                } else {
                    values[inputID] = field.defaultValue;
                }
            } else if (field.type === 'checkbox' && field.allowMultipleChoice) {
                values[inputID] = [];
            } else if (field.type === 'checkbox' && (!field.options || field.options.length === 0)) {
                values[inputID] = false;
            } else if (field.type === 'checkbox' && !field.allowMultipleChoice && field.options && field.options.length > 0) {
                values[inputID] = null;
            } else {
                values[inputID] = "";
            }
        });

        if (dataApplication.platform === 2) {
            values.exhibitor_id = editValues?.exhibitor_id ? Number(editValues.exhibitor_id) : null

            // Add ID for user edition
            if (editValues) {
                values.user_id = editValues.user_id;
                values.email = editValues.email || "";
                values.status = Number(editValues.status ?? 0);
                values.type = Number(editValues.type ?? type ?? 0)
            } else {
                values.email = "";
                values.type = Number(type ?? 0)
                values.status = 0
            }
        } else {
            if (editValues) {
                values.visitante_id = editValues.visitante_id;
            }
        }
        return values;
    };

    return (
        <Formik
            validationSchema={schemaCreator(fields, dataApplication.evento_id, editValues, dataApplication.platform, showFullForm, dataApplication.json?.form?.validEmails, true)}
            onSubmit={(values, formikHelpers) => {
                const normalizedValues = normalizeFormRfidValues(values, fields);
                return registerUser(
                    dataApplication.platform === 2 && showFullForm
                        ? { ...normalizedValues, status: 1 }
                        : normalizedValues,
                    formikHelpers,
                    type,
                    showFullForm,
                );
            }}
            innerRef={formikRef}
            validateOnChange={false}
            validateOnBlur={true}
            enableReinitialize
            initialValues={getInitialValues()}>
            {({
                handleSubmit,
                handleChange,
                values,
                touched,
                isValid,
                errors,
                dirty,
                setFieldValue,
            }) => (
                <Form noValidate onSubmit={handleSubmit} className="text-start">
                    <FormObserver fields={fields} dataApplication={dataApplication} />
                    <Row>
                        {type === 1 && <Form.Group as={Col} sm={12} controlId={'type'} className='mb-3'>
                            <Form.Label className="mb-0">Tipo de usuário</Form.Label>
                            <Form.Select
                                name="type"
                                value={values.type ?? 1}
                                onChange={(e) => {
                                    const nextType = Number(e.target.value);
                                    setFieldValue("type", nextType);
                                    if (nextType !== 2) {
                                        setFieldValue("exhibitor_id", null);
                                    }
                                }}
                                isInvalid={!!errors.type}
                                isValid={touched.type && !errors.type}
                            >
                                <option value={1}>Gestor</option>
                                <option value={2}>Expositor</option>
                            </Form.Select>
                            <div className="d-flex align-items-center mt-1">
                                <Form.Text className="text-muted me-2">Saiba mais sobre os tipos de usuário</Form.Text>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="type-tooltip">
                                            {Number(values.type) === 1 ? (
                                                <div className="text-start">
                                                    <b>Usuário Gestor:</b><br />
                                                    • Acesso à "Área de gestão"<br />
                                                    • Coleta de participantes<br />
                                                    • Sem Código QR (Não pode ser adicionado)
                                                </div>
                                            ) : (
                                                <div className="text-start">
                                                    <b>Usuário Expositor:</b><br />
                                                    • Pode editar informações da sua empresa<br />
                                                    • Coleta de participantes<br />
                                                    • Com Código QR de expositor
                                                </div>
                                            )}
                                        </Tooltip>
                                    }
                                >
                                    <span>
                                        <FontAwesomeIcon icon={faQuestionCircle} className="text-primary cursor-pointer" />
                                    </span>
                                </OverlayTrigger>
                            </div>
                            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                                {errors.type}
                            </Form.Control.Feedback>
                        </Form.Group>}
                        {type === 1 && Number(values.type) === 2 && (
                            <Form.Group as={Col} sm={12} controlId={"exhibitor_id"} className="mb-3">
                                <Form.Label className="mb-0">Expositor associado (opcional)</Form.Label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    options={availableExhibitors.map((exhibitor) => ({
                                        value: Number(exhibitor.id),
                                        label: exhibitor.name,
                                    }))}
                                    value={(() => {
                                        const selectedExhibitor = availableExhibitors.find(
                                            (exhibitor) => Number(exhibitor.id) === Number(values.exhibitor_id),
                                        );

                                        if (selectedExhibitor) {
                                            return {
                                                value: Number(selectedExhibitor.id),
                                                label: selectedExhibitor.name,
                                            };
                                        }

                                        if (values.exhibitor_id && editValues?.exhibitor_name) {
                                            return {
                                                value: Number(values.exhibitor_id),
                                                label: editValues.exhibitor_name,
                                            };
                                        }

                                        return null;
                                    })()}
                                    onChange={(option) => {
                                        setFieldValue("exhibitor_id", option?.value ?? null);
                                    }}
                                    placeholder={loadingAvailableExhibitors ? "Carregando expositores..." : "Selecione um expositor (opcional)..."}
                                    isDisabled={loadingAvailableExhibitors}
                                    isClearable
                                    noOptionsMessage={() => loadingAvailableExhibitors ? "Carregando expositores..." : "Nenhum expositor disponível"}
                                />
                                <Form.Text className="d-block">
                                    Vincule um expositor apenas se esta conta precisar representar um expositor específico e ter QR de expositor.
                                </Form.Text>
                            </Form.Group>
                        )}
                        {dataApplication.platform === 2 && <>
                            {!showFullFormStatus && <Form.Group as={Col} sm={12} controlId={'status'} className='mb-3'>
                                <Form.Label className="mb-0">{!editValues ? 'Tipo de registro' : 'Status do registro'}</Form.Label>
                                <Form.Select
                                    name="status"
                                    value={values.status}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value)
                                        setFieldValue(
                                            "status",
                                            value
                                        );
                                        if (value) {
                                            setShowFullForm(true)
                                        } else {
                                            setShowFullForm(false)
                                        }
                                    }}
                                    isInvalid={!!errors.status}
                                    isValid={touched.status && !errors.status}
                                >
                                    <option value="0">{'Criação do email'}</option>
                                    <option value="1">{'Completo'}</option>
                                </Form.Select>
                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                <Form.Control.Feedback type="invalid">
                                    {errors.status}
                                </Form.Control.Feedback>
                            </Form.Group>}
                            <Form.Group as={Col} sm={12} controlId={'email'} className='mb-3' key={'email-key'}>
                                <Form.Label className="mb-0">
                                    {dataApplication.json.form.emailLabel ? dataApplication.json.form.emailLabel : 'E-mail'}
                                    <span className="text-danger ms-1" aria-hidden="true">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="email"
                                    name={'email'}
                                    value={values.email}
                                    onChange={handleChange}
                                    aria-required={true}
                                    isInvalid={!!errors.email}
                                    isValid={touched.email && !errors.email}
                                />
                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                <Form.Control.Feedback type="invalid">
                                    {errors.email}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </>}
                        {showFullForm && <> {Object.keys(fields).map((key) => {
                            if (!isFieldVisible(fields[key], fields, values, new Set(), true)) {
                                return ""
                            }
                            const index = fields[key].inputID.toString()
                            switch (fields[key].type) {
                                case 'input':
                                    let mask = false
                                    let placeholder = false
                                    switch (fields[key].validation) {
                                        case 'telefone':
                                            mask = "(99) 9.9999-9999"
                                            placeholder = "(00) 0.0000-0000"
                                            break;
                                        case 'cpf':
                                            mask = "999.999.999-99"
                                            placeholder = "000.000.000-00"
                                            break;
                                        case 'cnpj':
                                            mask = "99.999.999/9999-99"
                                            placeholder = "00.000.000/0000-00"
                                            break;
                                        case 'date':
                                            mask = "99/99/9999"
                                            placeholder = "00/00/0000"
                                            break;

                                        default:
                                            break;
                                    }
                                    if (mask) {
                                        return (
                                            <Form.Group as={Col} sm={fields[key].col} controlId={'input' + key} className='mb-3' key={'key' + key}>
                                                <Form.Label className="mb-0">
                                                    <FieldLabel field={fields[key]} />
                                                    {fields[key].label !== `` && <p className="form-label small opacity-75">{fields[key].subLabel}</p>}
                                                    {fields[key].blockEditMessage && <p className="form-label small opacity-75">{fields[key].blockEditMessage}</p>}
                                                </Form.Label>
                                                <InputMask
                                                    placeholder={placeholder}
                                                    mask={mask}
                                                    value={values[index]}
                                                    onChange={fields[key].fieldWithUppercase ? (e) => {
                                                        const upperValue = e.target.value.toUpperCase();
                                                        setFieldValue(index, upperValue);
                                                    } : handleChange}
                                                    isInvalid={!!errors[index]}
                                                    isValid={touched[index] && !errors[index]}
                                                >
                                                    {(inputProps) => (
                                                        <Form.Control
                                                            {...inputProps}
                                                            type="text"
                                                            name={index}
                                                            aria-required={isFieldRequired(fields[key])}
                                                            className={fields[key].fieldWithUppercase ? 'uppercase-field' : ''}
                                                        />
                                                    )}
                                                </InputMask>
                                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors[index]}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        )
                                    } else {
                                        return (
                                            <Form.Group as={Col} sm={fields[key].col} controlId={'input' + key} className='mb-3' key={'key' + key}>
                                                <Form.Label className="mb-0">
                                                    <FieldLabel field={fields[key]} />
                                                    {fields[key].label !== `` && <p className="form-label small opacity-75">{fields[key].subLabel}</p>}
                                                    {fields[key].blockEditMessage && <p className="form-label small opacity-75">{fields[key].blockEditMessage}</p>}
                                                </Form.Label>
                                                <Form.Control
                                                    placeholder={fields[key].placeholder}
                                                    type="text"
                                                    name={index}
                                                    value={values[index]}
                                                    onChange={fields[key].fieldWithUppercase ? (e) => {
                                                        const upperValue = e.target.value.toUpperCase();
                                                        setFieldValue(index, upperValue);
                                                    } : handleChange}
                                                    aria-required={isFieldRequired(fields[key])}
                                                    isInvalid={!!errors[index]}
                                                    isValid={touched[index] && !errors[index]}
                                                    className={fields[key].fieldWithUppercase ? 'uppercase-field' : ''}
                                                />
                                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors[index]}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        )
                                    }
                                case 'textarea':
                                    return (
                                        <Form.Group as={Col} sm={fields[key].col} controlId={'textarea' + key} className='mb-3' key={'key' + key}>
                                            <Form.Label className="mb-0">
                                                <FieldLabel field={fields[key]} />
                                                {fields[key].label !== `` && <p className="form-label small opacity-75">{fields[key].subLabel}</p>}
                                                {fields[key].blockEditMessage && <p className="form-label small opacity-75">{fields[key].blockEditMessage}</p>}
                                            </Form.Label>
                                            <Form.Control
                                                placeholder={fields[key].placeholder}
                                                type="text"
                                                as="textarea"
                                                name={index}
                                                value={values[index]}
                                                onChange={fields[key].fieldWithUppercase ? (e) => {
                                                    const upperValue = e.target.value.toUpperCase();
                                                    setFieldValue(index, upperValue);
                                                } : handleChange}
                                                aria-required={isFieldRequired(fields[key])}
                                                isInvalid={!!errors[index]}
                                                isValid={touched[index] && !errors[index]}
                                                className={fields[key].fieldWithUppercase ? 'uppercase-field' : ''}
                                            />
                                            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                            <Form.Control.Feedback type="invalid">
                                                {errors[index]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    )
                                case 'select': {
                                    const selectOptions = (Array.isArray(fields[key].options) ? fields[key].options : [])
                                        .filter((valueX) => normalizeVacancyValue(valueX) !== '');
                                    const availableSelectOptions = selectOptions.filter((valueX) => {
                                        const availability = getVacancyAvailability(
                                            dataApplication,
                                            fields[key],
                                            valueX,
                                            values[index],
                                            Boolean(editValues),
                                        );
                                        return !availability || availability.isAvailable;
                                    });

                                    return (
                                        <Form.Group as={Col} sm={fields[key].col} controlId={'select' + key} className='mb-3' key={'key' + key}>
                                            <Form.Label className="mb-0">
                                                <FieldLabel field={fields[key]} />
                                                {fields[key].label !== `` && <p className="form-label small opacity-75">{fields[key].subLabel}</p>}
                                                {fields[key].blockEditMessage && <p className="form-label small opacity-75">{fields[key].blockEditMessage}</p>}
                                            </Form.Label>
                                            <Form.Select
                                                name={index}
                                                onChange={handleChange}
                                                value={values[index]}
                                                aria-required={isFieldRequired(fields[key])}
                                                isInvalid={!!errors[index]}
                                                isValid={touched[index] && !errors[index]}
                                            >
                                                {availableSelectOptions.length > 0 ? (
                                                    <>
                                                        <option value="" hidden>
                                                            Selecione uma opção
                                                        </option>
                                                        {availableSelectOptions.map((valueX, keyX) => (
                                                            <option key={"options" + keyX + valueX} value={valueX}>
                                                                {valueX}
                                                            </option>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <option disabled>Nenhuma opção disponível</option>
                                                )}
                                            </Form.Select>
                                            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                            <Form.Control.Feedback type="invalid">
                                                {errors[index]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    )
                                }
                                case 'checkbox':
                                    if (fields[key].options && fields[key].options.length > 0) {
                                        return (
                                            <Form.Group controlId={'checkbox' + key} className={`mb-2 mx-0 ${errors[index] ? 'is-invalid' : ''}`} key={'checkbox' + key}>
                                                <Form.Label className="mb-0">
                                                    <FieldLabel field={fields[key]} />
                                                    {fields[key].label !== `` && <p className="form-label small opacity-75">{fields[key].subLabel}</p>}
                                                    {fields[key].blockEditMessage && <p className="form-label small opacity-75">{fields[key].blockEditMessage}</p>}
                                                </Form.Label>
                                                {fields[key].options.map((option, indexX) => (
                                                    <Form.Check
                                                        className={`col mx-0 flex-0`} // Add 'is-invalid' class if there's an error
                                                        key={'check' + index + indexX}
                                                        id={`checkbox-${index}-${indexX}`}
                                                        type="checkbox"
                                                        label={option}
                                                        name="options"
                                                        value={values[index]}
                                                        aria-required={isFieldRequired(fields[key])}
                                                        checked={(values[index] && values[index].includes(option) && fields[key].allowMultipleChoice) || values[index] === option ? true : false}
                                                        onChange={(e) => {
                                                            if (fields[key].allowMultipleChoice) {
                                                                const newValues = values[index] ? [...values[index]] : [];
                                                                if (e.target.checked) {
                                                                    newValues.push(option);
                                                                } else {
                                                                    const idx = newValues.indexOf(option);
                                                                    if (idx > -1) newValues.splice(idx, 1);
                                                                }
                                                                setFieldValue(index, newValues);
                                                            } else {
                                                                // For single choice, toggle between the option and null/empty
                                                                if (e.target.checked) {
                                                                    setFieldValue(index, option);
                                                                } else {
                                                                    setFieldValue(index, null);
                                                                }
                                                            }
                                                        }}>
                                                    </Form.Check>
                                                ))}
                                                {errors[index] && <div className="invalid-feedback d-block">{errors[index]}</div>} {/* Display error message */}
                                            </Form.Group>
                                        );
                                    } else {
                                        return (
                                            <Form.Group as={Col} sm={fields[key].col} controlId={'input' + key} className='mb-2' key={'key' + key}>
                                                <Form.Check
                                                    className="checkboxFontSize"
                                                    checked={values[index] === true ? true : false}
                                                    type="checkbox"
                                                    value={values[index]}
                                                    name={index}
                                                    aria-required={isFieldRequired(fields[key])}
                                                    label={
                                                        <>
                                                            <FieldLabel field={fields[key]} />
                                                            {fields[key].label !== `` && <p className={`form-label small opacity-75`}>{fields[key].subLabel}</p>}
                                                            {fields[key].blockEditMessage && <p className="form-label small opacity-75">{fields[key].blockEditMessage}</p>}
                                                        </>}
                                                    onChange={(e) => {
                                                        setFieldValue(
                                                            index,
                                                            values[index] === true ? false : true
                                                        );
                                                    }}
                                                    isInvalid={!!errors[index]}
                                                    feedback={errors[index]}
                                                    feedbackType="invalid">
                                                </Form.Check>
                                            </Form.Group>
                                        )
                                    }
                                default:
                                    return ""
                            }
                        })}</>}
                    </Row>
                </Form>
            )}
        </Formik>
    )
}
